import { Badge2, BadgeType2, ButtonMenu2, Card2, ContentCol2, ContentColSize2, ContentRow2, IconFa, Input2, InputType2, LangTextV2, SortingColumn2, SortOrder2, TableWrapper2, Td2, Th2, Tooltip2, Tr2 } from 'bambooo';
import moment from 'moment';
import { Organization as OrganizationAPI } from '../Api/Organization';
import { Tours as ToursAPI } from '../Api/Tours';
import { Vehicle as VehicleAPI } from '../Api/Vehicle';
import { VehicleDriver as VehicleDriverAPI } from '../Api/VehicleDriver';
import { Lang } from '../Lang';
import { Nav } from '../Nav';
import { BasePage } from './BasePage';
import { TourEditModal } from './Tours/TourEditModal';
import { TourFilter } from './Tours/TourFilter';
import { ToursMap } from './Tours/TourMap';
import { TOURS_PAGE_NAME } from './PageNames';
const PAGE_SIZE = 50;
const escapeHtml = (s) => s
    .replace(/&/gu, '&amp;')
    .replace(/</gu, '&lt;')
    .replace(/>/gu, '&gt;')
    .replace(/"/gu, '&quot;')
    .replace(/'/gu, '&#39;');
const highlight = (text, term) => {
    if (text === '') {
        return '';
    }
    const escaped = escapeHtml(text);
    if (term === '') {
        return escaped;
    }
    const safe = term.replace(/[.*+?^${}()|[\]\\]/gu, '\\$&');
    return escaped.replace(new RegExp(safe, 'giu'), (match) => `<mark>${match}</mark>`);
};
/**
 * Tours
 */
export class Tours extends BasePage {
    constructor() {
        super();
        this._name = Tours.NAME;
        this._tourDialog = new TourEditModal();
    }
    /**
     * loadContent
     */
    async loadContent() {
        this.addNavbarButton('Add tour', 'fas fa-plus', () => {
            this._tourDialog.setTitle(new LangTextV2({ text: 'Add new tour' }));
            this._tourDialog.show();
        });
        // shared mutable state across loaders / renderers ----------------------------------------
        const sort = {
            id: SortOrder2.NONE,
            date: SortOrder2.DESC,
            tour_start: SortOrder2.DESC,
            tour_end: SortOrder2.NONE,
            create_datetime: SortOrder2.NONE,
            update_datetime: SortOrder2.NONE
        };
        const filterValues = {
            period_from: '',
            period_to: '',
            organization_id: 0,
            vehicle_id: 0,
            vehicle_driver_id: 0,
            search: '',
            only_without_tracks: false
        };
        let inHeaderSearch = '';
        const activeHighlight = () => inHeaderSearch === '' ? filterValues.search : inHeaderSearch;
        // Lookup maps populated once on first load and reused by the row renderer.
        const mvehicles = new Map();
        const mdrivers = new Map();
        const morganizations = new Map();
        const mdevices = new Map();
        const mcreaters = new Map();
        // Build the filter payload sent to the backend; drop empties.
        const buildApiFilter = (offset, limit) => {
            const apiFilter = {
                order: {
                    id: sort.id,
                    date: sort.date,
                    tour_start: sort.tour_start,
                    tour_end: sort.tour_end,
                    create_datetime: sort.create_datetime,
                    update_datetime: sort.update_datetime,
                    /*
                     * schema fields kept empty — server-side sort by computed
                     * counts is not implemented (counts are post-query batched)
                     */
                    count_sightings: '',
                    count_trackings: ''
                },
                limit,
                offset
            };
            if (filterValues.period_from !== '') {
                apiFilter.period_from = filterValues.period_from;
            }
            if (filterValues.period_to !== '') {
                apiFilter.period_to = filterValues.period_to;
            }
            if (filterValues.organization_id > 0) {
                apiFilter.organization_id = filterValues.organization_id;
            }
            if (filterValues.vehicle_id > 0) {
                apiFilter.vehicle_id = filterValues.vehicle_id;
            }
            if (filterValues.vehicle_driver_id > 0) {
                apiFilter.vehicle_driver_id = filterValues.vehicle_driver_id;
            }
            if (filterValues.only_without_tracks) {
                apiFilter.only_without_tracks = true;
            }
            const search = inHeaderSearch === '' ? filterValues.search : inHeaderSearch;
            if (search !== '') {
                apiFilter.search = search;
            }
            return apiFilter;
        };
        // Filter card ---------------------------------------------------------------------------
        const content = this._wrapper.getContentWrapper().getContent();
        const rowFilter = new ContentRow2(content.getContentFluidElement());
        const filter = new TourFilter();
        filter.appendTo(new ContentCol2(rowFilter, ContentColSize2.col12).getElement());
        // Lookup maps + initial loads -----------------------------------------------------------
        const [vehicles, drivers, organizations] = await Promise.all([
            VehicleAPI.getList(),
            VehicleDriverAPI.getList(),
            OrganizationAPI.getOrganizationByUser()
        ]);
        if (vehicles) {
            for (const v of vehicles) {
                mvehicles.set(v.id, v);
            }
            const activeVehicles = vehicles.filter((v) => v.in_use && !v.isdeleted);
            filter.setVehicleList(activeVehicles);
        }
        if (drivers) {
            for (const d of drivers) {
                mdrivers.set(d.id, d);
            }
            filter.setDriverList(drivers);
        }
        if (organizations) {
            for (const o of organizations) {
                morganizations.set(o.id, o);
            }
            filter.setOrganizationList(organizations);
        }
        // List card -----------------------------------------------------------------------------
        const row1 = new ContentRow2(content.getContentFluidElement());
        const card = new Card2({});
        card.appendTo(new ContentCol2(row1, ContentColSize2.col12).getElement());
        card.setTitle(new LangTextV2({ text: 'Tours' }));
        const btnMenu = new ButtonMenu2({ icon: IconFa.bars, positionRight: true });
        btnMenu.appendTo(card.getToolsElement());
        btnMenu.addMenuItem(new LangTextV2({ text: 'Filter' }), () => {
            filter.show();
        }, 'fa fa-filter');
        /*
         * In-header quick-search — sibling of .card-title in the card-header so AdminLTE's
         * float layout puts it directly right of the title.
         */
        const searchSlot = jQuery('<div class="sighting-quick-search"/>').insertAfter(card.getTitleElement());
        const searchInput = new Input2({ type: InputType2.text });
        searchInput.appendTo(searchSlot);
        searchInput.setPlaceholder(Lang.i().l('search persons / tour-fid …'));
        // Table ---------------------------------------------------------------------------------
        const tableWrapper = new TableWrapper2({ tableOptions: { head_fixed: true } });
        tableWrapper.appendTo(card.getElement());
        const table = tableWrapper.getTable();
        const trhead = new Tr2();
        trhead.appendTo(table.getThead());
        /*
         * Sort registry: each call wires a header to its key in `sort` and ensures clicking it
         * resets every other header (single-column sort).
         */
        const sortRegistry = [];
        const makeSortColumn = (label, key) => {
            const column = new LangTextV2({
                text: label,
                onClick: (langText) => {
                    const newValue = SortingColumn2.changeSort(langText, sort[key]);
                    for (const other of sortRegistry) {
                        if (other.key !== key) {
                            sort[other.key] = SortOrder2.NONE;
                            SortingColumn2.initSort(other.column, SortOrder2.NONE);
                        }
                    }
                    sort[key] = newValue;
                    tableWrapper.reset();
                }
            });
            SortingColumn2.initSort(column, sort[key]);
            sortRegistry.push({ key, column });
            return column;
        };
        const thIdDate = new Th2({});
        thIdDate.addComponent(makeSortColumn('Id', 'id'));
        thIdDate.append('<br>');
        thIdDate.addComponent(makeSortColumn('Date', 'date'));
        thIdDate.appendTo(trhead);
        const thVehicle = new Th2({});
        thVehicle.addComponent(new LangTextV2({ text: 'Vehicle' }));
        thVehicle.append('<br>');
        thVehicle.addComponent(new LangTextV2({ text: 'Driver' }));
        thVehicle.append('<br>');
        thVehicle.addComponent(new LangTextV2({ text: 'Organization' }));
        thVehicle.appendTo(trhead);
        const thTime = new Th2({});
        thTime.addComponent(makeSortColumn('Time begin-end', 'tour_start'));
        thTime.append('<br>');
        thTime.addComponent(new LangTextV2({ text: 'Sightings' }));
        thTime.appendTo(trhead);
        new Th2({ value: new LangTextV2({ text: 'Tracking points' }) }).appendTo(trhead);
        new Th2({ value: new LangTextV2({ text: 'Persons on boat' }) }).appendTo(trhead);
        const thCreated = new Th2({});
        thCreated.addComponent(new LangTextV2({ text: 'from Device' }));
        thCreated.append('<br>');
        thCreated.addComponent(new LangTextV2({ text: 'Created by' }));
        thCreated.append('<br>');
        thCreated.addComponent(makeSortColumn('Created', 'create_datetime'));
        thCreated.appendTo(trhead);
        new Th2({ value: '' }).appendTo(trhead);
        // Renderer ------------------------------------------------------------------------------
        const renderRow = (_table, entry) => {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q;
            const term = activeHighlight();
            const trbody = new Tr2();
            trbody.appendTo(table.getTbody());
            const date = moment((_a = entry.date) === null || _a === void 0 ? void 0 : _a.split(' ')[0]);
            new Td2({ value: `<b>#${entry.id}</b><br>${date.format('YYYY.MM.DD')}` }).appendTo(trbody);
            const vehicleName = (_c = (_b = mvehicles.get(entry.vehicle_id)) === null || _b === void 0 ? void 0 : _b.name) !== null && _c !== void 0 ? _c : '';
            const driverName = (_e = (_d = mdrivers.get(entry.vehicle_driver_id)) === null || _d === void 0 ? void 0 : _d.user.name) !== null && _e !== void 0 ? _e : '';
            const orgStr = entry.organization_id ? (_g = (_f = morganizations.get(entry.organization_id)) === null || _f === void 0 ? void 0 : _f.description) !== null && _g !== void 0 ? _g : '' : '';
            new Td2({
                value: `${escapeHtml(vehicleName)}<br>${escapeHtml(driverName)}<br>${escapeHtml(orgStr)}`
            }).appendTo(trbody);
            const tdTimeCount = new Td2({ value: `<b>${escapeHtml((_h = entry.tour_start) !== null && _h !== void 0 ? _h : '')} - ${escapeHtml((_j = entry.tour_end) !== null && _j !== void 0 ? _j : '')}</b><br>` });
            tdTimeCount.appendTo(trbody);
            new Badge2({
                text: `${entry.count_sightings}`,
                type: entry.count_sightings > 0 ? BadgeType2.success : BadgeType2.secondary
            }).appendTo(tdTimeCount);
            const tdTrackingCount = new Td2({ value: '' });
            tdTrackingCount.appendTo(trbody);
            if (entry.count_trackings > 0) {
                const badgeTracking = new Badge2({ text: `${entry.count_trackings}`, type: BadgeType2.info });
                badgeTracking.appendTo(tdTrackingCount);
                badgeTracking.getElement().on('click', () => {
                    Nav.load(new ToursMap(entry.id)).then();
                });
                badgeTracking.getElement().css({ cursor: 'pointer' });
            }
            else {
                tdTrackingCount.addValue(`${entry.count_trackings}`);
            }
            /*
             * Persons on boat — JSON list, render as comma-separated names. Search highlight
             * applies so the in-header search clearly tells the user *why* a row matched.
             */
            let personsStr = '';
            try {
                const persons = JSON.parse((_k = entry.record_by_persons) !== null && _k !== void 0 ? _k : '');
                if (Array.isArray(persons)) {
                    personsStr = persons.map((p) => typeof p === 'string' ? p : '').filter((p) => p !== '').join(', ');
                }
                else if (persons && typeof persons === 'object') {
                    personsStr = Object.values(persons).map((p) => typeof p === 'string' ? p : '').filter((p) => p !== '').join(', ');
                }
            }
            catch (_r) {
                personsStr = (_l = entry.record_by_persons) !== null && _l !== void 0 ? _l : '';
            }
            new Td2({ value: highlight(personsStr, term) }).appendTo(trbody);
            const deviceName = (_o = (_m = mdevices.get(entry.device_id)) === null || _m === void 0 ? void 0 : _m.name) !== null && _o !== void 0 ? _o : 'not set';
            const createrName = (_q = (_p = mcreaters.get(entry.creater_id)) === null || _p === void 0 ? void 0 : _p.name) !== null && _q !== void 0 ? _q : 'not set';
            const createDate = moment(entry.create_datetime * 1000);
            new Td2({
                value: `${escapeHtml(deviceName)}<br>${escapeHtml(createrName)}<br><b>${createDate.format('YYYY.MM.DD HH:mm:ss')}</b>`
            }).appendTo(trbody);
            const tdAction = new Td2({ value: '' });
            tdAction.appendTo(trbody);
            const abtnMenu = new ButtonMenu2({ icon: IconFa.bars, positionRight: true });
            abtnMenu.appendTo(tdAction);
            abtnMenu.addMenuItem(Lang.i().l('Show Map'), () => {
                Nav.load(new ToursMap(entry.id)).then();
            }, IconFa.hockeypuck);
            abtnMenu.addMenuItem(Lang.i().l('Delete'), () => {
                // eslint-disable-next-line no-alert
                window.alert('Delete todo');
            }, IconFa.trash);
        };
        // Hook the data source — TableWrapper drives infinite scroll itself ----------------------
        this._onLoadTable = async () => {
            await tableWrapper.reset();
        };
        tableWrapper.setDataSource(async (page) => {
            var _a;
            card.showLoading();
            try {
                const offset = page * PAGE_SIZE;
                const response = await ToursAPI.getList(buildApiFilter(offset, PAGE_SIZE));
                if (!response || !response.list) {
                    return [];
                }
                if (response.devices) {
                    for (const d of response.devices) {
                        mdevices.set(d.id, d);
                    }
                }
                if (response.creaters) {
                    for (const c of response.creaters) {
                        mcreaters.set(c.id, c);
                    }
                }
                const count = (_a = response.count) !== null && _a !== void 0 ? _a : 0;
                card.setTitle(`${Lang.i().l('Tours')} (${count})`);
                return response.list;
            }
            finally {
                card.hideLoading();
                Lang.i().lAll();
                Tooltip2.init();
            }
        }, renderRow);
        // Wire the in-header quick-search (debounced) -------------------------------------------
        let searchTimer = null;
        searchInput.getElement().on('keyup', () => {
            if (searchTimer !== null) {
                clearTimeout(searchTimer);
            }
            searchTimer = setTimeout(() => {
                const value = searchInput.getValue().trim();
                if (value === inHeaderSearch) {
                    return;
                }
                inHeaderSearch = value;
                tableWrapper.reset();
            }, 300);
        });
        // Wire filter Apply / Reset -------------------------------------------------------------
        filter.setOnApply((values) => {
            filterValues.period_from = values.period_from;
            filterValues.period_to = values.period_to;
            filterValues.organization_id = values.organization_id;
            filterValues.vehicle_id = values.vehicle_id;
            filterValues.vehicle_driver_id = values.vehicle_driver_id;
            filterValues.search = values.search;
            tableWrapper.reset();
        });
    }
}
Tours.NAME = TOURS_PAGE_NAME;
